import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Key, Plus, Copy, Trash2, X, Loader2, Check, Eye, EyeOff,
  RefreshCw, Shield, Clock, Activity, Code, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function ApiKeyManagement() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/developer/keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(response.data.api_keys || []);
    } catch (err) {
      // Silently handle 404 errors (endpoint may not exist for this user's role)
      if (err.response?.status !== 404 && err.response?.status !== 403) {
        console.error('Failed to load API keys:', err);
      }
      // Don't show toast for missing endpoint or permission errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const createApiKey = async () => {
    if (!keyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/developer/keys`, {
        name: keyName.trim(),
        permissions: ['read'],
        expires_in_days: 365
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewKeyData(response.data);
      setShowCreateModal(false);
      setShowNewKeyModal(true);
      setKeyName('');
      fetchApiKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const revokeApiKey = async (keyId, keyName) => {
    if (!confirm(`Are you sure you want to revoke "${keyName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/developer/keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('API key revoked');
      fetchApiKeys();
    } catch (err) {
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = async (text, keyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(keyId);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div className="space-y-4" data-testid="api-key-management">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center">
            <Key className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Developer API</h2>
            <p className="text-xs text-zinc-500">Manage API keys for external integrations</p>
          </div>
        </div>
        <div className="flex gap-2">
          <a 
            href="/api/v1/docs" 
            target="_blank"
            className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors"
          >
            <Code className="h-4 w-4" />
            API Docs
            <ExternalLink className="h-3 w-3" />
          </a>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white"
            data-testid="create-api-key-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create API Key
          </Button>
        </div>
      </div>

      {/* API Info Card */}
      <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-emerald-400 mt-0.5" />
          <div className="text-sm">
            <p className="text-white font-medium mb-1">External API Access</p>
            <p className="text-zinc-400">
              Create API keys to allow external applications to access athlete data. 
              Include your API key in the <code className="bg-zinc-800 px-1 rounded">X-API-Key</code> header.
            </p>
            <p className="text-zinc-500 mt-2">
              Base URL: <code className="bg-zinc-800 px-1 rounded text-emerald-400">https://app.eliteplayerperformance.co.uk/api/v1</code>
            </p>
          </div>
        </div>
      </div>

      {/* API Keys List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/50 rounded-xl border border-zinc-800">
          <Key className="h-12 w-12 mx-auto mb-3 text-zinc-600" />
          <p className="text-zinc-400 mb-4">No API keys yet</p>
          <Button
            onClick={() => setShowCreateModal(true)}
            variant="outline"
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create your first API key
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {apiKeys.map((key) => (
            <div
              key={key.id}
              className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800 hover:border-zinc-700 transition-all"
              data-testid={`api-key-${key.id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${key.is_active ? 'bg-emerald-500/20' : 'bg-zinc-700'}`}>
                    <Key className={`h-5 w-5 ${key.is_active ? 'text-emerald-400' : 'text-zinc-500'}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-white truncate">{key.name}</p>
                      {key.is_active ? (
                        <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">Active</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-zinc-700 text-zinc-400 rounded-full text-xs">Inactive</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-zinc-500 mt-1">
                      <code className="bg-zinc-800 px-2 py-0.5 rounded text-zinc-400">{key.key_prefix}</code>
                      <span className="flex items-center gap-1">
                        <Activity className="h-3 w-3" />
                        {key.usage_count || 0} requests
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="hidden sm:block text-right text-sm">
                    <div className="flex items-center gap-1 text-zinc-500">
                      <Clock className="h-3 w-3" />
                      <span>Expires: {formatDate(key.expires_at)}</span>
                    </div>
                    {key.last_used_at && (
                      <p className="text-zinc-600 text-xs">Last used: {formatDate(key.last_used_at)}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revokeApiKey(key.id, key.name)}
                    className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                    title="Revoke API key"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Plus className="h-5 w-5 text-emerald-400" />
                <h3 className="text-lg font-bold text-white">Create API Key</h3>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Key Name *</label>
                <Input
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="e.g., My App Integration"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="api-key-name-input"
                />
                <p className="text-xs text-zinc-500 mt-1">A friendly name to identify this key</p>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <p className="text-sm text-zinc-400 mb-2">This API key will have:</p>
                <ul className="text-sm text-zinc-300 space-y-1">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-emerald-400" />
                    Read access to athletes, teams, and tests
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-emerald-400" />
                    1000 requests per hour rate limit
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-emerald-400" />
                    365 day expiration
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={createApiKey}
                disabled={creating || !keyName.trim()}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white disabled:opacity-50"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
                Create Key
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* New Key Created Modal */}
      {showNewKeyModal && newKeyData && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-emerald-500/30 rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <Check className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">API Key Created!</h3>
                <p className="text-sm text-zinc-400">Save it now - it won't be shown again</p>
              </div>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-4 mb-4">
              <label className="text-xs text-zinc-500 mb-2 block">Your API Key:</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-zinc-900 rounded px-3 py-2 text-emerald-400 font-mono text-sm break-all">
                  {newKeyData.api_key}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(newKeyData.api_key, 'new')}
                  className="text-zinc-400 hover:text-white"
                >
                  {copiedKey === 'new' ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-400">
                <strong>Important:</strong> This is the only time you'll see this key. 
                Copy it and store it securely.
              </p>
            </div>
            
            <div className="text-sm text-zinc-400 space-y-1">
              <p><strong>Name:</strong> {newKeyData.name}</p>
              <p><strong>Expires:</strong> {formatDate(newKeyData.expires_at)}</p>
            </div>
            
            <Button
              onClick={() => {
                setShowNewKeyModal(false);
                setNewKeyData(null);
              }}
              className="w-full mt-6 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white"
            >
              I've Saved My Key
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
